import type React from "react";
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { LoginPage } from "@/features/auth/LoginPage.tsx";
import { RegisterPage } from "@/features/auth/RegisterPage.tsx";
import { authStatusQuery } from "@/features/auth/session.ts";
import { FirstPasswordPage } from "@/features/auth/FirstPasswordPage.tsx";
import { SetupPage } from "@/features/auth/SetupPage.tsx";
import { VerifyPage } from "@/features/auth/VerifyPage.tsx";
import { SettingsHub } from "@/features/settings/SettingsHub.tsx";
import { ConsentPage } from "@/features/auth/ConsentPage.tsx";
import { AppShell } from "@/features/shell/AppShell.tsx";
import { ProjectHome } from "@/features/projects/ProjectHome.tsx";
import { ProjectPage } from "@/features/projects/ProjectPage.tsx";
import { ProjectSettingsPage } from "@/features/projects/ProjectSettingsPage.tsx";
import { ChatIndex, ChatPage } from "@/features/chat/ChatPage.tsx";
import { CommandCenter } from "@/features/shell/CommandCenter.tsx";
import { AgentStudio, AgentsList } from "@/features/studio/AgentStudio.tsx";
import { AgentWizard } from "@/features/studio/AgentWizard.tsx";
import { GroupsPage } from "@/features/studio/GroupsPage.tsx";
import { SkillsPage } from "@/features/studio/SkillsPage.tsx";
import { SpaceHome, SpaceLayout, usePersonalSpace } from "@/features/space/SpacePage.tsx";
import { MultiChatPage } from "@/features/space/MultiChat.tsx";
import { McpPage } from "@/features/space/McpPage.tsx";
import { SpaceDetail, SpacesList } from "@/features/space/Spaces.tsx";
import { SchedulesPanel } from "@/features/dock/SchedulesPanel.tsx";
import { useTranslation } from "react-i18next";
import { chatsApi } from "@/api/chats.ts";
import { useQuery } from "@tanstack/react-query";
import { projectsApi } from "@/api/projects.ts";
import { queryClient } from "@/queryClient.ts";
import { PageTitle, Spinner } from "@/ui/primitives.tsx";
import { WorkflowsPage } from "@/features/space/Workflows.tsx";

const status = () => queryClient.ensureQueryData(authStatusQuery);

const rootRoute = createRootRoute({
  component: Outlet,
  pendingComponent: () => (
    <div className="grid h-full place-items-center">
      <Spinner />
    </div>
  ),
});

// ---- public (unauthenticated) routes ----
const publicGuard = async (to: string) => {
  const s = await status();
  if (s.setupRequired && to !== "/setup") throw redirect({ to: "/setup" });
  if (!s.setupRequired && to === "/setup") throw redirect({ to: "/login" });
  if (s.user) throw redirect({ to: "/" });
};

const setupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/setup", beforeLoad: () => publicGuard("/setup"), component: SetupPage });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", beforeLoad: () => publicGuard("/login"), component: LoginPage });
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  validateSearch: z.object({ invite: z.string().optional() }),
  beforeLoad: () => publicGuard("/register"),
  component: function Register() {
    const { invite } = registerRoute.useSearch();
    return <RegisterPage initialInvite={invite ?? ""} />;
  },
});
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
  validateSearch: z.object({ pending: z.string(), sentTo: z.string().default(""), expiresAt: z.string().optional() }),
  beforeLoad: () => publicGuard("/verify"),
  component: function Verify() {
    const { pending, sentTo } = verifyRoute.useSearch();
    return <VerifyPage pending={pending} sentTo={sentTo} />;
  },
});

/** OAuth consent: needs a signed-in user, but lives outside the app shell. */
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth/consent",
  beforeLoad: async () => {
    const s = await status();
    if (s.setupRequired) throw redirect({ to: "/setup" });
    if (!s.user) {
      sessionStorage.setItem("codvero.afterLogin", location.pathname + location.search);
      throw redirect({ to: "/login" });
    }
    return { user: s.user };
  },
  component: function Consent() {
    const { user } = consentRoute.useRouteContext();
    return <ConsentPage user={user} />;
  },
});

// ---- authenticated shell ----
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    const s = await status();
    if (s.setupRequired) throw redirect({ to: "/setup" });
    if (!s.user) throw redirect({ to: "/login" });
    return { user: s.user, singleProject: s.singleProject };
  },
  component: function App() {
    const { user } = appRoute.useRouteContext();
    // an account made by an admin chooses its own password first (the server refuses everything else until then)
    if (user.mustChangePassword) return <FirstPasswordPage user={user} />;
    return <AppShell user={user} />;
  },
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: function HomeRoute() {
    const { user, singleProject } = appRoute.useRouteContext();
    // Codvero Lab: one project; home is that project (the usual page until it exists, to create it)
    const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, enabled: singleProject });
    if (singleProject && !projects.data) return null;
    const only = singleProject ? projects.data?.find((p) => p.kind !== "personal") : undefined;
    if (only) return <Navigate to="/projects/$projectId" params={{ projectId: only.id }} replace />;
    return <CommandCenter user={user} />;
  },
});
const Scroll = ({ children }: { children: React.ReactNode }) => <div className="h-full overflow-y-auto"><div className="mx-auto max-w-5xl px-4 py-6 sm:px-8">{children}</div></div>;
const projectsRoute = createRoute({ getParentRoute: () => appRoute, path: "/projects", component: () => <Navigate to="/" replace /> });
const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$projectId",
  validateSearch: z.object({ dock: z.enum(["team", "providers", "browser", "terminal", "files", "git", "note", "memory", "schedules", "services"]).optional() }),
  component: function ProjectRoute() {
    const { projectId } = projectRoute.useParams();
    return <ProjectPage projectId={projectId} />;
  },
});
const projectOverview = createRoute({ getParentRoute: () => projectRoute, path: "/", component: () => <ProjectHome projectId={projectRoute.useParams().projectId} /> });
const projectMembers = createRoute({ getParentRoute: () => projectRoute, path: "/members", beforeLoad: ({ params }) => { throw redirect({ to: "/projects/$projectId/settings", params: { projectId: (params as { projectId: string }).projectId }, hash: "project-sharing" }); } });
const projectSecrets = createRoute({ getParentRoute: () => projectRoute, path: "/secrets", beforeLoad: ({ params }) => { throw redirect({ to: "/projects/$projectId/settings", params: { projectId: (params as { projectId: string }).projectId }, hash: "project-secrets" }); } });
const projectChat = createRoute({ getParentRoute: () => projectRoute, path: "/chat", component: () => <ChatIndex projectId={projectRoute.useParams().projectId} /> });
const projectChatOne = createRoute({
  getParentRoute: () => projectRoute,
  path: "/chat/$chatId",
  component: function ChatOne() {
    const { projectId, chatId } = projectChatOne.useParams();
    return <ChatPage key={chatId} projectId={projectId} chatId={chatId} />;
  },
});
const projectSettings = createRoute({ getParentRoute: () => projectRoute, path: "/settings", component: () => <ProjectSettingsPage projectId={projectRoute.useParams().projectId} /> });
// the Chat section: conversations in the user's personal space (a hidden, unlimited container of their own)
const spaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat",
  validateSearch: z.object({ dock: z.enum(["team", "providers", "browser", "terminal", "files", "git", "note", "memory", "schedules"]).optional() }),
  component: SpaceLayout,
});
const spaceHome = createRoute({ getParentRoute: () => spaceRoute, path: "/", component: () => <SpaceHome /> });
const spaceTemporary = createRoute({ getParentRoute: () => spaceRoute, path: "/temporary", component: () => <SpaceHome temporary /> });
// the section's own skills and MCP connections (static paths win over /$chatId)
const SpacePage = ({ children }: { children: React.ReactNode }) => <div className="h-full overflow-y-auto"><div className="mx-auto max-w-5xl px-4 py-6 sm:px-8">{children}</div></div>;
const spaceSkills = createRoute({ getParentRoute: () => spaceRoute, path: "/skills", component: () => <SpacePage><SkillsPage scope="chat" /></SpacePage> });
const spaceSpaces = createRoute({ getParentRoute: () => spaceRoute, path: "/spaces", component: () => <SpacePage><SpacesList /></SpacePage> });
const spaceSpace = createRoute({ getParentRoute: () => spaceRoute, path: "/spaces/$spaceId", component: function SpaceOne() { const { spaceId } = spaceSpace.useParams(); return <SpacePage><SpaceDetail key={spaceId} spaceId={spaceId} /></SpacePage>; } });
const spaceSchedules = createRoute({
  getParentRoute: () => spaceRoute,
  path: "/schedules",
  component: function SpaceSchedules() {
    const space = usePersonalSpace().data;
    const { t } = useTranslation();
    return <SpacePage><PageTitle title={t("dock.schedules")} subtitle={t("spaces.schedulesSubtitle")} /><div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface">{space ? <SchedulesPanel projectId={space.id} canEdit chatSection /> : <Spinner />}</div></SpacePage>;
  },
});
const spaceWorkflows = createRoute({ getParentRoute: () => spaceRoute, path: "/workflows", component: () => <SpacePage><WorkflowsPage /></SpacePage> });
const spaceMcp = createRoute({ getParentRoute: () => spaceRoute, path: "/mcp", component: () => <SpacePage><McpPage /></SpacePage> });
const spaceChat = createRoute({
  getParentRoute: () => spaceRoute,
  path: "/$chatId",
  component: function SpaceChat() {
    const { chatId } = spaceChat.useParams();
    const space = usePersonalSpace().data;
    // a multi-provider chat opens as columns, an ordinary one as the usual chat page
    const chat = useQuery({ queryKey: ["chat-kind", chatId], queryFn: () => chatsApi.get(chatId), staleTime: Infinity });
    if (!space || !chat.data) return null;
    return chat.data.kind === "multi" ? <MultiChatPage key={chatId} chatId={chatId} /> : <ChatPage key={chatId} projectId={space.id} chatId={chatId} />;
  },
});
const agentsRoute = createRoute({ getParentRoute: () => appRoute, path: "/agents", component: AgentStudio });
const agentsIndex = createRoute({ getParentRoute: () => agentsRoute, path: "/", component: AgentsList });
const agentsNew = createRoute({ getParentRoute: () => agentsRoute, path: "/new", component: () => <AgentWizard agentId={null} /> });
const agentsGroups = createRoute({ getParentRoute: () => agentsRoute, path: "/groups", component: GroupsPage });
const agentsSkills = createRoute({ getParentRoute: () => agentsRoute, path: "/skills", component: SkillsPage });
const agentsOne = createRoute({ getParentRoute: () => agentsRoute, path: "/$agentId", component: function AgentOne() { const { agentId } = agentsOne.useParams(); return <AgentWizard key={agentId} agentId={agentId} />; } });

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: function SettingsRoute() {
    const { user } = appRoute.useRouteContext();
    return <Scroll><SettingsHub user={user} /></Scroll>;
  },
});
/** Old tab URLs keep working: they land on the matching section of the single settings page. */
const settingsAlias = (path: string, section: string) =>
  createRoute({ getParentRoute: () => settingsRoute, path, beforeLoad: () => { throw redirect({ to: "/settings", hash: `settings-${section}` }); } });
const settingsIndex = createRoute({ getParentRoute: () => settingsRoute, path: "/", component: () => null });
const settingsAppearance = settingsAlias("/appearance", "appearance");
const settingsNotifications = settingsAlias("/notifications", "notifications");
const settingsConnections = settingsAlias("/connections", "info");
const settingsAccounts = settingsAlias("/accounts", "agents");
const settingsUsers = settingsAlias("/users", "users");
const settingsUpdates = settingsAlias("/updates", "updates");

const routeTree = rootRoute.addChildren([
  consentRoute,
  setupRoute,
  loginRoute,
  registerRoute,
  verifyRoute,
  appRoute.addChildren([homeRoute, projectsRoute, projectRoute.addChildren([projectOverview, projectChat, projectChatOne, projectMembers, projectSecrets, projectSettings]), spaceRoute.addChildren([spaceHome, spaceTemporary, spaceSkills, spaceMcp, spaceSpaces, spaceSpace, spaceSchedules, spaceWorkflows, spaceChat]), agentsRoute.addChildren([agentsIndex, agentsNew, agentsGroups, agentsSkills, agentsOne]), settingsRoute.addChildren([settingsIndex, settingsAppearance, settingsNotifications, settingsConnections, settingsAccounts, settingsUsers, settingsUpdates])]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
